import React, { useCallback, useMemo, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import isEmpty from 'lodash/isEmpty';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
	type CustomTriggerProps,
	DropdownItem,
	DropdownItemCheckbox,
	DropdownItemGroup,
	DropdownItemCheckboxGroup,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import logger from '@atlassian/jira-common-util-logging/src/log';

import { useIntl } from '@atlassian/jira-intl';
import { useChildIssuesLimit, useIssueKey } from '@atlassian/jira-issue-context-service';
import { HIDE_DONE_SUBTASKS_FILTER } from '@atlassian/jira-issue-shared-types';
import { useUserPreferencesValue } from '@atlassian/jira-issue-user-preference-services';
import type { ChildIssuesLimitStatus } from '@atlassian/jira-issue-view-common-constants';
import {
	type ChildIssue,
	getChildIssuesLimitExceededAnalyticsCount,
} from '@atlassian/jira-issue-view-common-types/src/children-issues-type';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	useProjectKey,
	useProjectType,
	useIsSimplifiedProject,
} from '@atlassian/jira-project-context-service';
import { useRouterActions } from '@atlassian/jira-router';
import type { IssueKey } from '@atlassian/jira-shared-types';
import { generateViewInSearchLink } from '../../../common/generate-view-in-search-link';
import {
	hasExceededIssuesLimitAfterLoad,
	hasExceededIssuesLimitInitialLoad,
} from '../../../common/util';
import { useChildIssuesSortBy } from '../../../hooks';
import type { ChildIssuesPanelType } from '../../../model/types';
import messages from './messages';
import { generateBulkEditLink } from './utils';

export type Props = {
	childIssuesPanelType: ChildIssuesPanelType;
	parentKey: IssueKey;
	hideDone?: boolean;
	allIssues: ChildIssue[];
	incompleteIssues: ChildIssue[];
	childIssuesLimitStatus: ChildIssuesLimitStatus;
	onHideDoneClicked: (
		totalIssues: number,
		incompleteIssues: number,
		hideDone: boolean,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	onBulkEditClicked: (issueCount: number | string, analyticsEvent: UIAnalyticsEvent) => void;
	onViewInSearchClicked: (issueCount: number | string, analyticsEvent: UIAnalyticsEvent) => void;
};

const testId = 'issue.views.common.child-issues-panel.heading.meatballs.dropdown.trigger-button';

export const Meatballs = ({
	childIssuesLimitStatus,
	childIssuesPanelType,
	parentKey,
	hideDone = false,
	allIssues,
	incompleteIssues,
	onHideDoneClicked,
	onBulkEditClicked,
	onViewInSearchClicked,
}: Props) => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const [sortByValue] = useChildIssuesSortBy();

	const [hideDonePreference, { setValue: setHideDonePreference }] =
		useUserPreferencesValue(HIDE_DONE_SUBTASKS_FILTER);

	const issueKey = useIssueKey();

	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const isSimplifiedProject = useIsSimplifiedProject(projectKey);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onBulkEditClickedAnalytics = useCallback(
		(issueCount: number | string) => (analyticsEvent: UIAnalyticsEvent) => {
			onBulkEditClicked(issueCount, analyticsEvent);
		},
		[onBulkEditClicked],
	);

	const onViewInSearchClickedAnalytics = useCallback(
		(issueCount: number | string) => (analyticsEvent: UIAnalyticsEvent) => {
			onViewInSearchClicked(issueCount, analyticsEvent);
		},
		[onViewInSearchClicked],
	);

	const onHideDoneClickedWithMyPreferences = useCallback(() => {
		// myPreferences can only be set as a string, not as a boolean
		const hasHideDonePreference = hideDonePreference === 'true';
		setHideDonePreference(String(!hasHideDonePreference));

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'item',
			attributes: {
				childIssuesPanelType,
				totalIssues: allIssues.length,
				incompleteIssues: incompleteIssues.length,
				hideDone: !hasHideDonePreference,
			},
		});

		onHideDoneClicked(
			allIssues.length,
			incompleteIssues.length,
			!hasHideDonePreference,
			analyticsEvent,
		);
	}, [
		allIssues.length,
		childIssuesPanelType,
		createAnalyticsEvent,
		hideDonePreference,
		incompleteIssues.length,
		onHideDoneClicked,
		setHideDonePreference,
	]);

	const isWithinLimitOnInitialLoad = useMemo(
		() => !hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus),
		[childIssuesLimitStatus],
	);

	const isHideDoneCheckboxVisible = useMemo(
		() =>
			!isEmpty(incompleteIssues) &&
			incompleteIssues.length !== allIssues.length &&
			isWithinLimitOnInitialLoad,
		[allIssues.length, isWithinLimitOnInitialLoad, incompleteIssues],
	);

	const viewInSearchLink = useMemo(() => {
		// These values will never be missing when the FF is enabled, this check is to satisfy TS
		if (projectType === undefined || isSimplifiedProject === undefined) {
			logger.safeErrorWithoutCustomerData(
				'issue.child-issues-panel',
				'Missing project, type, or isSimplified when generating search link',
			);
			return '';
		}

		return generateViewInSearchLink(
			projectKey,
			projectType,
			isSimplifiedProject,
			parentKey,
			childIssuesPanelType,
			hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus),
			sortByValue,
		);
	}, [
		childIssuesLimitStatus,
		childIssuesPanelType,
		isSimplifiedProject,
		parentKey,
		projectKey,
		projectType,
		sortByValue,
	]);

	const onViewInSearchClickedCallback = useCallback(
		(visibleIssuesCount: number | string) =>
			(event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
				onViewInSearchClickedAnalytics(visibleIssuesCount)(analyticsEvent);
				// Ignore cmd or ctrl clicks - allow user to open in new tab
				if (!event.metaKey && !event.ctrlKey) {
					event.preventDefault();
					push(viewInSearchLink);
				}
			},
		[push, onViewInSearchClickedAnalytics, viewInSearchLink],
	);

	const renderHideDoneCheckbox = useCallback(() => {
		const hasExceededLimitAfterLoad = hasExceededIssuesLimitAfterLoad(childIssuesLimitStatus);

		const dropdownItemCheckbox = (
			<DropdownItemCheckbox
				id="hideDone"
				key="hideDone"
				onClick={onHideDoneClickedWithMyPreferences}
				isSelected={hideDonePreference === 'true'}
				isDisabled={hasExceededLimitAfterLoad}
			>
				{formatMessage(messages.hideDone)}
			</DropdownItemCheckbox>
		);

		return isHideDoneCheckboxVisible ? (
			<DropdownItemCheckboxGroup
				title={formatMessage(messages.filter)}
				id="child-issues-panel-checkbox-group"
			>
				{hasExceededLimitAfterLoad ? (
					<Tooltip content={formatMessage(messages.disabledHideDone)} position="top">
						{dropdownItemCheckbox}
					</Tooltip>
				) : (
					dropdownItemCheckbox
				)}
			</DropdownItemCheckboxGroup>
		) : null;
	}, [
		childIssuesLimitStatus,
		formatMessage,
		hideDonePreference,
		isHideDoneCheckboxVisible,
		onHideDoneClickedWithMyPreferences,
	]);

	const childIssuesLimit = useChildIssuesLimit();

	const childIssuesLimitExceededAnalyticsCount =
		getChildIssuesLimitExceededAnalyticsCount(childIssuesLimit);

	const renderedActionLinks = useMemo(() => {
		const visibleIssues = hideDone ? incompleteIssues : allIssues;
		const visibleIssuesCount = hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus)
			? childIssuesLimitExceededAnalyticsCount
			: visibleIssues.length;
		const bulkEditLink = generateBulkEditLink(
			parentKey,
			childIssuesPanelType,
			hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus),
			sortByValue || undefined,
		);

		const actionsTitle = isHideDoneCheckboxVisible ? formatMessage(messages.actions) : undefined;

		return (
			<DropdownItemGroup
				testId="issue-view-common-views.child-issues-panel.heading.meatballs.dropdown-item-group"
				title={actionsTitle}
			>
				<DropdownItem
					// @ts-expect-error Type '(analyticsEvent: AnalyticsEvent) => void' is not assignable to type '(e: MouseEvent<Element, MouseEvent> | KeyboardEvent<Element>) => void'.
					onClick={onBulkEditClickedAnalytics(visibleIssuesCount)}
					href={bulkEditLink}
					/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
					data-testid="issue.views.common.child-issues-panel.heading.meatballs.bulk-edit-dropdown-item"
				>
					{formatMessage(messages.bulkEdit)}
				</DropdownItem>
				<DropdownItem
					// @ts-expect-error - TS2769 - No overload matches this call.
					onClick={onViewInSearchClickedCallback(visibleIssuesCount)}
					href={viewInSearchLink}
					/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
					data-testid="issue.views.common.child-issues-panel.heading.meatballs.view-in-search-dropdown-item"
				>
					{formatMessage(messages.search)}
				</DropdownItem>
			</DropdownItemGroup>
		);
	}, [
		allIssues,
		childIssuesLimitStatus,
		childIssuesLimitExceededAnalyticsCount,
		childIssuesPanelType,
		formatMessage,
		hideDone,
		incompleteIssues,
		isHideDoneCheckboxVisible,
		onBulkEditClickedAnalytics,
		onViewInSearchClickedCallback,
		parentKey,
		sortByValue,
		viewInSearchLink,
	]);

	const renderDropdownTriggerButton = useCallback(
		({ triggerRef, ...triggerProps }: CustomTriggerProps<HTMLButtonElement>) => (
			<IconButton
				{...triggerProps}
				appearance="subtle"
				spacing="compact"
				icon={MoreIcon}
				UNSAFE_size="small"
				label={formatMessage(messages.tooltip)}
				ref={triggerRef}
				interactionName="auto-generated.jira-issue-view-common-views.child-issues-panel.view.heading.meatballs.button"
			/>
		),
		[formatMessage],
	);

	return (
		<ButtonWrapper data-testid={testId}>
			<DropdownMenu placement="bottom-end" trigger={renderDropdownTriggerButton}>
				{renderHideDoneCheckbox()}
				{renderedActionLinks}
			</DropdownMenu>
		</ButtonWrapper>
	);
};

export default Meatballs;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.childIssuesPanelMeatballButton': {
		padding: `0 ${token('space.050', '4px')}`,
	},
});
